/**
 * GatewaySecurity — Gateway > Security Posture.
 *
 * The read-only reframe of the old "Security Center" placeholder. A uCentral whitebox gateway has
 * no IDS/IPS and reports no intrusion, malware or reputation data (see ComingSoon "security-center"
 * for that measurement), so this is NOT an event feed. It is a read-time AGGREGATION: the backend
 * (services/gateway_posture) re-reads three surfaces we already expose — the gateway firewall,
 * its routing tables and its WAN watchdog — and diffs them against a small fixed rule set to
 * produce operator-facing FINDINGS, one card per gateway.
 *
 * Like the Route Table, the underlying routes read costs an SSH round trip per gateway, so the
 * screen fetches once and offers an explicit Refresh rather than polling.
 *
 * Findings arrive severity-sorted (warn first) from the backend; the card renders them in that
 * order and colours each with the reserved status ramp via StatusPill — never brand colour.
 */
import { useSession } from "@/stores/session";
import { useGatewayPosture } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import type { GatewayPostureEntry, PostureFinding } from "@/types/domain";

export default function GatewaySecurity() {
  const siteId = useSession((s) => s.currentSiteId);
  const { data, isLoading, isFetching, error, refetch } = useGatewayPosture(siteId);

  if (!siteId) {
    return (
      <div>
        <PageHeader title="Security Posture" subtitle="Edge security posture for the site's gateways" />
        <EmptyState
          title="Select a site"
          hint="Posture is read from a site's gateways. Pick a site in the scope bar."
        />
      </div>
    );
  }

  const gateways = data?.gateways ?? [];
  const summary = data?.summary;

  return (
    <div>
      <PageHeader
        title="Security Posture"
        subtitle="Firewall, routing and WAN-remediation posture, aggregated from each gateway — read-only"
        actions={
          <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? "Reading…" : "Refresh"}
          </Button>
        }
      />

      {/* The roll-up: the one-glance answer above the per-gateway detail. Zero warnings is itself
          worth stating plainly, so the screen reads as "checked and clear", not "empty". */}
      {summary && gateways.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
          <StatusPill status={summary.warn_count > 0 ? "warn" : "healthy"}>
            {summary.warn_count} {summary.warn_count === 1 ? "warning" : "warnings"}
          </StatusPill>
          <StatusPill status="info">
            {summary.info_count} {summary.info_count === 1 ? "note" : "notes"}
          </StatusPill>
          <span>across {gateways.length} {gateways.length === 1 ? "gateway" : "gateways"}</span>
        </div>
      )}

      {error && <ErrorBanner error={error} />}

      {isLoading && (
        <div className="text-sm text-ink-muted">
          Reading each gateway's firewall, routes and watchdog — this takes a moment.
        </div>
      )}

      {data && gateways.length === 0 && (
        <EmptyState
          title="No gateways at this site"
          hint="Security posture is read from gateways. Adopt one and it will appear here."
        />
      )}

      <div className="flex flex-col gap-4">
        {gateways.map((gw) => (
          <GatewayCard key={gw.serial} gw={gw} />
        ))}
      </div>
    </div>
  );
}

function GatewayCard({ gw }: { gw: GatewayPostureEntry }) {
  const warnCount = gw.findings.filter((f) => f.severity === "warn").length;
  return (
    <section className="md-card p-4">
      <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <h2 className="text-base font-semibold text-ink">{gw.name}</h2>
          <span className="font-mono text-xs text-ink-subtle">{gw.serial}</span>
        </div>
        {/* A gateway with a warning is flagged in the header so a long list is scannable. A clean
            gateway says so rather than showing nothing, which reads as "not checked". */}
        <StatusPill status={warnCount > 0 ? "warn" : "healthy"}>
          {warnCount > 0
            ? `${warnCount} ${warnCount === 1 ? "warning" : "warnings"}`
            : "No warnings"}
        </StatusPill>
      </header>

      {gw.findings.length === 0 ? (
        <p className="text-sm text-ink-muted">
          Nothing to report — firewall, routing and WAN watchdog all look sound on this gateway.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {gw.findings.map((f, i) => (
            <FindingRow key={`${f.code}-${i}`} finding={f} />
          ))}
        </ul>
      )}
    </section>
  );
}

function FindingRow({ finding }: { finding: PostureFinding }) {
  return (
    <li className="flex items-start gap-3 rounded border border-line bg-surface-2 px-3 py-2">
      {/* The severity pill carries the reserved ramp colour; "warn" -> Degraded, "info" -> Info. */}
      <StatusPill status={finding.severity} className="mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-sm text-ink">{finding.message}</p>
        {/* The stable code is shown small and monospaced so a finding is greppable in a report
            without cluttering the human-readable message above it. */}
        <p className="mt-0.5 font-mono text-[0.625rem] uppercase tracking-wide text-ink-subtle">
          {finding.code}
        </p>
      </div>
    </li>
  );
}
